import { Link } from 'react-router';
import {
  ChannelIcon,
  CopyButton,
  Dialog,
  Empty,
  ErrorState,
  Loading,
  PageHead,
  Select,
  StatusBadge,
  useFormat,
} from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Lock, PlugZap, RefreshCw, Trash2, Wifi } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '../crm/forms';
import {
  ConnectButton,
  ConnectReturn,
  iconFor,
  useConnectors,
  type ConnectorInfo,
} from '../integrations/Connect';
import { api } from '../lib/api';
import { useTeamName, useTeams } from './Teams';

interface ProviderField {
  key: string;
  kind: 'text' | 'secret' | 'email' | 'number' | 'url' | 'select';
  required: boolean;
  generated?: boolean;
  options?: string[];
  placeholder?: string;
}
interface ProviderDef {
  provider: string;
  channel: string;
  connector?: string;
  manual?: boolean;
  uses?: string[];
  status: 'available' | 'planned';
  phase?: number;
  webhook: boolean;
  fields: ProviderField[];
}
interface Account {
  id: string;
  channel: string;
  provider: string;
  displayName: string;
  externalId: string;
  config: Record<string, string | number>;
  status: 'pending' | 'active' | 'error' | 'disabled';
  lastVerifiedAt: string | null;
  lastError: string | null;
  webhookUrl: string | null;
  defaultTeamId: string | null;
  widget: { key: string; scriptUrl: string } | null;
  secrets: { name: string; hint: string | null }[];
  connectMethod: 'oauth' | 'manual';
  connector: string | null;
  sync: {
    status: string;
    phase: string;
    counts: Record<string, number>;
    error: string | null;
    finishedAt: string | null;
  } | null;
}
interface Catalogue {
  catalogue: ProviderDef[];
  accounts: Account[];
  /** LY Voice can be connected in one click on this deployment. */
  lyVoiceOneClick?: boolean;
}

const INPUT_TYPE: Record<string, string> = {
  secret: 'password',
  email: 'email',
  number: 'number',
  url: 'url',
  text: 'text',
};

/** Form for a provider's fields. On edit, secret inputs are blank and mean "keep the stored value". */
function ProviderForm({
  def,
  values,
  setValues,
  editing,
}: {
  def: ProviderDef;
  values: Record<string, string>;
  setValues: (v: Record<string, string>) => void;
  editing: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="form-grid">
      {def.fields
        .filter((f) => !f.generated)
        .map((f) => {
          const id = `pf-${f.key}`;
          const label = `${t(`crm.integrations.field.${f.key}`)}${f.required ? '' : ` (${t('common.optional')})`}`;
          return (
            <Field
              key={f.key}
              id={id}
              label={label}
              hint={f.kind === 'secret' && editing ? t('crm.integrations.keepSecret') : undefined}
            >
              {f.kind === 'select' ? (
                <Select
                  id={id}
                  value={values[f.key] ?? f.options?.[0] ?? ''}
                  onChange={(v) => setValues({ ...values, [f.key]: v })}
                  options={(f.options ?? []).map((o) => ({
                    value: o,
                    label: t(`crm.integrations.security.${o}`, { defaultValue: o }),
                  }))}
                />
              ) : (
                <input
                  id={id}
                  className={`input${f.kind === 'secret' ? ' mono' : ''}`}
                  dir="ltr"
                  type={INPUT_TYPE[f.kind]}
                  autoComplete={f.kind === 'secret' ? 'new-password' : 'off'}
                  required={f.required && !(editing && f.kind === 'secret')}
                  placeholder={f.placeholder}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                />
              )}
            </Field>
          );
        })}
    </div>
  );
}

function GeneratedDialog({ values, onClose }: { values: Record<string, string>; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog
      open
      title={t('crm.integrations.generatedTitle')}
      onClose={onClose}
      testId="generated-secret"
      footer={
        <button className="btn btn-grad" onClick={onClose}>
          {t('common.close')}
        </button>
      }
    >
      <p className="mute" style={{ marginTop: 0 }}>
        {t('crm.integrations.generatedHint')}
      </p>
      {Object.entries(values).map(([k, v]) => (
        <Field key={k} id={`gen-${k}`} label={t(`crm.integrations.field.${k}`)}>
          <div className="code-box">
            <code id={`gen-${k}`} data-testid={`generated-${k}`}>
              {v}
            </code>
            <CopyButton value={v} />
          </div>
        </Field>
      ))}
    </Dialog>
  );
}

function AccountCard({
  a,
  def,
  connector,
  onGenerated,
}: {
  a: Account;
  def?: ProviderDef;
  connector?: ConnectorInfo;
  onGenerated: (v: Record<string, string>) => void;
}) {
  const oauth = a.connectMethod === 'oauth';
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{ ok: boolean; detail?: string } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['integrations'] });
  const teams = useTeams();
  const teamName = useTeamName();
  const routing = useMutation({
    mutationFn: (defaultTeamId: string | null) =>
      api(`/v1/integrations/${a.id}/routing`, { method: 'PUT', body: JSON.stringify({ defaultTeamId }) }),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: () =>
      api<{ result: { ok: boolean; detail?: string } }>(`/v1/integrations/${a.id}/test`, { method: 'POST' }),
    onSuccess: (r) => {
      setResult(r.result);
      refresh();
    },
  });
  const save = useMutation({
    mutationFn: () => {
      const submitted = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== ''));
      return api(`/v1/integrations/${a.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ values: submitted }),
      });
    },
    onSuccess: () => {
      setEditing(false);
      setResult(null);
      refresh();
    },
  });
  const regen = useMutation({
    mutationFn: (field: string) =>
      api<{ generated: Record<string, string> }>(`/v1/integrations/${a.id}/regenerate/${field}`, {
        method: 'POST',
      }),
    onSuccess: (r) => onGenerated(r.generated),
  });
  const remove = useMutation({
    mutationFn: () => api(`/v1/integrations/${a.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const generatedFields = def?.fields.filter((f) => f.generated) ?? [];

  return (
    <section className="card card-pad provider-card" data-testid={`account-${a.provider}`}>
      <div className="top">
        <span className="channel-badge">
          <ChannelIcon channel={iconFor(a.provider)} size={14} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong dir="auto">{a.displayName}</strong>
          <div className="faint mono" style={{ fontSize: 'var(--t-xs)' }} dir="ltr">
            {t(`crm.integrations.providers.${a.provider}`)} · {a.externalId}
          </div>
        </div>
        <StatusBadge value={a.status} ns="crm.integrations.status" />
      </div>
      {a.lastVerifiedAt && (
        <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
          {t('crm.integrations.lastVerified', { when: fmt.relative(a.lastVerifiedAt) })}
        </div>
      )}
      {a.status === 'error' && a.lastError && (
        <div className="form-error">{t('crm.integrations.testFailed', { detail: a.lastError })}</div>
      )}
      {result?.ok && (
        <div className="notice success" role="status">
          {t('crm.integrations.testOk')}
        </div>
      )}
      {a.provider === 'ly_voice' && a.status === 'active' && <LyVoiceUsage accountId={a.id} />}
      {a.webhookUrl && (
        <Field
          id={`wh-${a.id}`}
          label={t('crm.integrations.webhookUrl')}
          hint={t('crm.integrations.webhookHint')}
        >
          <div className="code-box">
            <code id={`wh-${a.id}`} data-testid="webhook-url">
              {a.webhookUrl}
            </code>
            <CopyButton value={a.webhookUrl} />
          </div>
        </Field>
      )}
      {!!teams.data?.items.length && (
        <Field
          id={`team-${a.id}`}
          label={t('crm.integrations.defaultTeam')}
          hint={t('crm.integrations.defaultTeamHint')}
        >
          <Select
            id={`team-${a.id}`}
            value={a.defaultTeamId ?? ''}
            disabled={routing.isPending}
            onChange={(v) => routing.mutate(v || null)}
            data-testid="default-team"
            options={[
              { value: '', label: t('crm.integrations.noDefaultTeam') },
              ...teams.data.items.map((x) => ({ value: x.id, label: teamName(x) })),
            ]}
          />
        </Field>
      )}
      {routing.isError && <ErrorState error={routing.error} />}
      {a.widget && (
        <Field
          id={`embed-${a.id}`}
          label={t('crm.integrations.embedCode')}
          hint={t('crm.integrations.embedHint')}
        >
          <div className="code-box">
            <code id={`embed-${a.id}`} data-testid="embed-code">
              {embedSnippet(a.widget)}
            </code>
            <CopyButton value={embedSnippet(a.widget)} />
          </div>
        </Field>
      )}
      {a.sync && (
        <p
          className={`notice ${a.sync.status === 'failed' ? 'warn' : a.sync.status === 'done' ? 'success' : ''}`}
          data-testid="store-sync"
        >
          <span className="grow">
            {t(`connect.sync.${a.sync.status}`, {
              products: a.sync.counts.products ?? 0,
              customers: a.sync.counts.customers ?? 0,
              orders: a.sync.counts.orders ?? 0,
            })}
            {a.sync.error && <span className="faint"> ({a.sync.error})</span>}
          </span>
        </p>
      )}
      {oauth && (
        <p className="faint connected-by" data-testid="connected-by-signin">
          <Lock size={12} />
          {t('connect.byPlatform', { platform: t(`connect.platform.${a.connector}`) })}
        </p>
      )}
      <div hidden={oauth}>
        {a.secrets.map((s) => (
          <div key={s.name} className="secret-row">
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <Lock size={12} className="faint" />
              {t(`crm.integrations.field.${s.name}`, { defaultValue: s.name })}
            </span>
            <span className="mono faint">{s.hint}</span>
          </div>
        ))}
        <p className="faint" style={{ fontSize: '0.72rem', margin: '0.4rem 0 0' }}>
          {t('crm.integrations.secretsHint')}
        </p>
      </div>
      <div className="quick-actions">
        <button
          className="btn btn-sm"
          onClick={() => test.mutate()}
          disabled={test.isPending}
          data-testid="test-connection"
        >
          <Wifi size={14} />
          {t('crm.integrations.test')}
        </button>
        {a.provider === 'whatsapp_cloud' && (
          <Link to="/admin/whatsapp" className="btn btn-sm" data-testid="whatsapp-manage">
            {t('whatsapp.manage')}
          </Link>
        )}
        {oauth && connector ? (
          <ConnectButton
            connector={connector}
            label={t('connect.reconnect')}
            primary={false}
            testId="reconnect"
          />
        ) : a.config.managed ? null : (
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
            <KeyRound size={14} />
            {t('crm.integrations.edit')}
          </button>
        )}
        {!oauth &&
          generatedFields.map((f) => (
            <button key={f.key} className="btn btn-ghost btn-sm" onClick={() => regen.mutate(f.key)}>
              <RefreshCw size={14} />
              {t('crm.integrations.regenerate')}: {t(`crm.integrations.field.${f.key}`)}
            </button>
          ))}
        <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(true)}>
          <Trash2 size={14} />
          {t('crm.integrations.disconnect')}
        </button>
      </div>
      {editing && def && (
        <Dialog
          open
          wide
          title={`${t('crm.integrations.edit')} · ${a.displayName}`}
          onClose={() => setEditing(false)}
          onSubmit={() => save.mutate()}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-grad" disabled={save.isPending}>
                {t('common.save')}
              </button>
            </>
          }
        >
          <ProviderForm
            def={def}
            values={{
              ...Object.fromEntries(Object.entries(a.config).map(([k, v]) => [k, String(v)])),
              ...values,
            }}
            setValues={setValues}
            editing
          />
          {save.isError && <ErrorState error={save.error} />}
        </Dialog>
      )}
      {confirm && (
        <Dialog
          open
          title={t('crm.integrations.disconnect')}
          onClose={() => setConfirm(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setConfirm(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                style={{ ['--btn-bg' as string]: 'var(--danger)', ['--btn-fg' as string]: '#fff' }}
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {t('crm.integrations.disconnect')}
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t('crm.integrations.disconnectConfirm', { name: a.displayName })}</p>
        </Dialog>
      )}
    </section>
  );
}

function ConnectDialog({
  def,
  onClose,
  onGenerated,
}: {
  def: ProviderDef;
  onClose: () => void;
  onGenerated: (v: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(t(`crm.integrations.providers.${def.provider}`));
  const [values, setValues] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: () => {
      const v: Record<string, string | number> = {};
      for (const f of def.fields) {
        const raw = values[f.key] ?? (f.kind === 'select' ? f.options?.[0] : undefined);
        if (raw !== undefined && raw !== '') v[f.key] = f.kind === 'number' ? Number(raw) : raw;
      }
      return api<{ generated: Record<string, string> }>('/v1/integrations', {
        method: 'POST',
        body: JSON.stringify({ provider: def.provider, displayName: name, values: v }),
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['integrations'] });
      onClose();
      if (Object.keys(r.generated).length) onGenerated(r.generated);
    },
  });
  return (
    <Dialog
      open
      wide
      title={`${t('crm.integrations.connect')} · ${t(`crm.integrations.providers.${def.provider}`)}`}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      testId="connect-dialog"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-grad" disabled={m.isPending}>
            {t('crm.integrations.connect')}
          </button>
        </>
      }
    >
      <p className="mute" style={{ marginTop: 0 }}>
        {t(`crm.integrations.help.${def.provider}`)}
      </p>
      <Field id="pf-name" label={t('crm.integrations.displayName')}>
        <input
          id="pf-name"
          className="input"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <ProviderForm def={def} values={values} setValues={setValues} editing={false} />
      <p className="faint" style={{ fontSize: 'var(--t-xs)', display: 'flex', gap: 6, alignItems: 'center' }}>
        <Lock size={12} />
        {t('crm.integrations.secretsHint')}
      </p>
      {m.isError && <ErrorState error={m.error} />}
    </Dialog>
  );
}

const CATEGORIES = ['messaging', 'stores', 'social', 'email', 'other'] as const;
const categoryOf = (d: ProviderDef): (typeof CATEGORIES)[number] =>
  d.channel === 'store'
    ? 'stores'
    : d.channel === 'email'
      ? 'email'
      : d.uses && !d.uses.includes('messages')
        ? 'social'
        : ['voice', 'sms'].includes(d.channel) || d.status === 'planned'
          ? 'other'
          : 'messaging';

export function IntegrationsPage() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['integrations'],
    queryFn: () => api<Catalogue>('/v1/integrations'),
    // While a store import runs, show its progress.
    refetchInterval: (query) =>
      query.state.data?.accounts.some((a) => a.sync && ['queued', 'running'].includes(a.sync.status))
        ? 3000
        : false,
  });
  const connectors = useConnectors();
  const [connect, setConnect] = useState<ProviderDef | null>(null);
  const [generated, setGenerated] = useState<Record<string, string> | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} />;
  const defs = q.data.catalogue;
  const connectorOf = (key?: string | null) => connectors.data?.connectors.find((c) => c.key === key);
  return (
    <>
      <PageHead
        eyebrow={t('nav.admin')}
        title={t('crm.integrations.title')}
        subtitle={t('crm.integrations.subtitle')}
      />
      <ConnectReturn />
      <h2 className="section-title">{t('crm.integrations.connected')}</h2>
      {q.data.accounts.length ? (
        <div
          className="provider-grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 380px), 1fr))',
            marginBottom: '1.5rem',
          }}
        >
          {q.data.accounts.map((a) => (
            <AccountCard
              key={a.id}
              a={a}
              def={defs.find((d) => d.provider === a.provider)}
              connector={connectorOf(a.connector)}
              onGenerated={setGenerated}
            />
          ))}
        </div>
      ) : (
        <section className="card" style={{ marginBottom: '1.5rem' }}>
          <Empty text={t('crm.integrations.noneConnected')} />
        </section>
      )}
      <h2 className="section-title">{t('crm.integrations.available')}</h2>
      <p className="mute" style={{ marginTop: '-0.25rem' }}>
        {t('connect.howItWorks')}
      </p>
      {CATEGORIES.map((cat) => {
        const list = defs.filter((d) => categoryOf(d) === cat);
        if (!list.length) return null;
        return (
          <div key={cat} style={{ marginBottom: '1.25rem' }}>
            <h3 className="eyebrow" style={{ margin: '0.75rem 0 0.5rem' }}>
              {t(`connect.category.${cat}`)}
            </h3>
            <div className="provider-grid">
              {list.map((d) => {
                const c = connectorOf(d.connector);
                return (
                  <section
                    key={d.provider}
                    className="card card-pad provider-card"
                    data-testid={`provider-${d.provider}`}
                  >
                    <div className="top">
                      <span className="channel-badge">
                        <ChannelIcon channel={iconFor(d.provider)} size={14} />
                      </span>
                      <strong style={{ flex: 1 }}>{t(`crm.integrations.providers.${d.provider}`)}</strong>
                    </div>
                    <p className="mute" style={{ margin: 0, fontSize: 'var(--t-sm)', flex: 1 }}>
                      {t(`crm.integrations.help.${d.provider}`)}
                    </p>
                    {d.status === 'available' ? (
                      <div className="connect-routes">
                        {d.provider === 'ly_voice' &&
                          q.data.lyVoiceOneClick &&
                          !q.data.accounts.some((a) => a.provider === 'ly_voice' && a.config.managed) && (
                            <LyVoiceConnect />
                          )}
                        {c && (
                          <ConnectButton
                            connector={c}
                            label={t('connect.connect')}
                            testId={`connect-${d.provider}`}
                          />
                        )}
                        {d.manual !== false && (
                          <button
                            className={`btn btn-sm${c || (d.provider === 'ly_voice' && q.data.lyVoiceOneClick) ? ' btn-ghost' : ''}`}
                            onClick={() => setConnect(d)}
                            data-testid={`manual-${d.provider}`}
                          >
                            <PlugZap size={14} />
                            {c || (d.provider === 'ly_voice' && q.data.lyVoiceOneClick)
                              ? t(d.provider === 'ly_voice' ? 'lyVoice.ownKey' : 'connect.manual')
                              : t('crm.integrations.connect')}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="badge badge-neutral" style={{ alignSelf: 'flex-start' }}>
                        {t('crm.integrations.planned', { phase: d.phase })}
                      </span>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        );
      })}
      {connect && <ConnectDialog def={connect} onClose={() => setConnect(null)} onGenerated={setGenerated} />}
      {generated && <GeneratedDialog values={generated} onClose={() => setGenerated(null)} />}
    </>
  );
}

/** The tag a site pastes before </body>. The key is public; the server only accepts calls from allowed origins. */
function embedSnippet(w: { key: string; scriptUrl: string }) {
  return `<script src="${w.scriptUrl}" data-key="${w.key}" async></script>`;
}

/** LY Voice in one click: LY360 sets up this workspace's own LY Voice account and connects it. */
function LyVoiceConnect() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const go = useMutation({
    mutationFn: () => api('/v1/integrations/ly-voice/connect', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['integrations'] }),
  });
  return (
    <>
      <button
        className="btn btn-grad btn-sm"
        onClick={() => go.mutate()}
        disabled={go.isPending}
        data-testid="connect-ly_voice"
      >
        <PlugZap size={14} />
        {go.isPending ? t('lyVoice.connecting') : t('connect.connect')}
      </button>
      {go.isError && <ErrorState error={go.error} />}
    </>
  );
}

/** This month's LY Voice calls, talk time and charge, as LY Voice bills them. */
function LyVoiceUsage({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const q = useQuery({
    queryKey: ['lyvoice-usage', accountId],
    queryFn: () =>
      api<{ usage: { calls: number; seconds: number; charge?: number; currency?: string } }>(
        `/v1/integrations/${accountId}/usage`,
      ),
    staleTime: 60_000,
  });
  if (!q.data) return null;
  const u = q.data.usage;
  return (
    <dl className="kv" data-testid="lyvoice-usage">
      <dt>{t('lyVoice.usage.calls')}</dt>
      <dd>{fmt.number(u.calls)}</dd>
      <dt>{t('lyVoice.usage.minutes')}</dt>
      <dd>{fmt.number(Math.round(u.seconds / 60))}</dd>
      {u.charge != null && u.currency && (
        <>
          <dt>{t('lyVoice.usage.charge')}</dt>
          <dd>{fmt.money(Math.round(u.charge * 100), u.currency)}</dd>
        </>
      )}
    </dl>
  );
}
